import { describeDidAttribution, DidAttributionSource, DID_RUNG_LABELS } from '../utils/didAttribution'

/**
 * WHICH DID THIS FAX ARRIVED FOR, AND FROM WHAT SOURCE — one renderer, both detail surfaces.
 *
 * Requirement: `.kiro/specs/DID-AUTHORITY-LADDER.md`. Run detail and batch item detail both render
 * THIS component rather than each formatting the columns themselves, for the reason
 * `api/runs.timestamp_provenance` is shared by both: two surfaces describing one row's authority
 * differently is the failure the ladder exists to prevent, and it would surface as two views of
 * the same fax disagreeing about how much to trust its attribution.
 *
 * IT STATES THE SOURCE ALWAYS, AND WARNS NEVER. PR #174 rendered an amber warning panel for the
 * rungs we judged shaky; the operator declined that framing — *"I dont think we need to display a
 * WARNING, but we should be clear about where the authoritative timestamp came from."* So the
 * source is named on every recorded attribution, in the same plain vocabulary the per-timestamp
 * provenance rows on these pages already use (`components/Hint.TimestampRow`), rather than in a
 * third visual language of its own.
 *
 * A CONFLICT IS STILL SHOWN AS A PROBLEM. Comparable rungs disagreeing means a misconfigured job
 * or a misrouted fax — a fault in the world, not a weakly-sourced reading — and declining warnings
 * is not declining error reporting (spec rule 4). That case, and only that case, is coloured.
 *
 * A ROW WITH NOTHING RECORDED SAYS SO. Every fax measured before these columns existed holds NULL
 * and renders "Not recorded" — never a source, never a fault. Labelling a historical measurement
 * row would be inventing a measurement (Decision 2), and it is worse for an attribution than for a
 * timestamp: the rung is the reader's only warrant for the number beside it.
 */
export function DidAttributionPanel({ row }: { row: DidAttributionSource }) {
  const d = describeDidAttribution(row)

  if (d.notRecorded) {
    return (
      <div style={{ fontSize: '0.82rem', color: '#9aa0a6' }}>
        <strong style={{ color: '#9aa0a6', fontWeight: 500 }}>Arrival DID</strong>{' '}
        <span>— not recorded.</span>{' '}
        <span>
          No DID attribution was stored for this fax. That is the permanent answer for every fax
          measured before the ladder recorded its decision, and it is not a fault reading: it means
          nothing was recorded, never that no DID was involved.
        </span>
      </div>
    )
  }

  const detail = (row.did_attribution_detail || {}) as Record<string, Record<string, string>>
  const byRung = detail.by_rung || {}
  const skipped = detail.skipped || {}
  const conflict = detail.conflict_between || {}

  return (
    <div
      style={{
        fontSize: '0.82rem',
        // Coloured for a FAULT only. A rung-3 attribution is a plain statement of its source and
        // gets the same surface as a rung-1 one — colouring it would be the warning the operator
        // declined, worn as a border.
        border: `1px solid ${d.fault ? '#7f2f2f' : '#2c3a2c'}`,
        background: d.fault ? '#2a0f0f' : 'transparent',
        borderRadius: 4,
        padding: '0.6rem 0.7rem',
      }}
    >
      <div style={{ marginBottom: '0.35rem' }}>
        <strong style={{ color: d.fault ? '#ef9a9a' : '#9aa0a6', fontWeight: d.fault ? 700 : 500 }}>
          Arrival DID
        </strong>
        {d.did && (
          <span style={{ fontFamily: 'monospace', marginLeft: '0.5rem', color: '#e8eaed' }}>
            {d.did}
          </span>
        )}
        {/* THE SOURCE, ALWAYS. Same shape as the `tag` on a TimestampRow, deliberately: an
            operator reading either surface is answering the same question. */}
        <span
          style={{
            marginLeft: '0.5rem',
            fontSize: '0.68rem',
            padding: '0 0.3rem',
            borderRadius: 3,
            border: `1px solid ${d.fault ? '#7f2f2f' : '#333333'}`,
            color: d.fault ? '#ef9a9a' : '#9aa0a6',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
          }}
        >
          {d.sourceShort}
        </span>
      </div>
      <div style={{ color: d.fault ? '#ef9a9a' : '#9aa0a6' }}>{d.text}</div>

      {/* THE EVIDENCE, not just the verdict. A rung that AGREED is corroboration and a later
          disagreement is only diagnosable against what used to agree; a rung that was SKIPPED is
          a different fact depending on WHY, which is why the reason is printed rather than the
          rung merely being absent. */}
      {Object.keys(conflict).length > 0 && (
        <ul style={{ margin: '0.45rem 0 0', paddingLeft: '1.1rem', color: '#ef9a9a' }}>
          {Object.entries(conflict).map(([rung, value]) => (
            <li key={rung}>
              <code>{rung}</code> said <code>{value}</code>
            </li>
          ))}
        </ul>
      )}
      {Object.keys(byRung).length > 0 && (
        <ul style={{ margin: '0.45rem 0 0', paddingLeft: '1.1rem', color: '#9aa0a6' }}>
          {Object.entries(byRung).map(([rung, value]) => (
            <li key={rung}>
              <code>{value}</code> — {DID_RUNG_LABELS[rung] || rung}
              {rung === d.rung ? ' (used)' : ' (agreed)'}
            </li>
          ))}
        </ul>
      )}
      {Object.keys(skipped).length > 0 && (
        <details style={{ marginTop: '0.4rem', color: '#9aa0a6' }}>
          <summary style={{ cursor: 'pointer' }}>
            Rungs that supplied nothing ({Object.keys(skipped).length}), and why
          </summary>
          <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
            {Object.entries(skipped).map(([rung, why]) => (
              <li key={rung}>
                <code>{rung}</code> — {why}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}
